import axios from "axios";
import { CompanyComment } from "interfaces/companies";
import { useEffect, useState, useRef } from "react";
import { Avatar, Card, Col, Input, Row, Typography, Button, Modal } from "antd";
import { CommentOutlined, TeamOutlined, BankOutlined, DeleteOutlined } from "@ant-design/icons";
import fallbackAvatar from "assets/images/fallbackAvatar.png";
import dayjs from "dayjs";
import "dayjs/locale/nb";
import useViewport from "components/UseViewport/UseViewport";
import styled from "styled-components";
import { useLoginContext } from "contexts/LoginContext/LoginContext";
import { message } from "components/Messages/Messages";

dayjs.locale("nb");

interface CompanyCommentsProps {
  companyId: number;
}

const ChatContainer = styled.div`
  height: 500px;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const ChatBubble = styled.div<{ isMobile: boolean; isOwnComment: boolean }>`
  background-color: ${props => (props.isOwnComment ? "var(--primary-color)" : "var(--neutral-alter)")};
  color: ${props => (props.isOwnComment ? "white" : "inherit")};
  border-radius: 18px;
  padding: 12px 16px;
  padding-bottom: 24px;
  margin: 8px 0;
  max-width: ${props => (props.isMobile ? "80%" : "60%")};
  position: relative;
  margin-left: ${props => (props.isOwnComment ? "auto" : "0")};
  margin-right: ${props => (props.isOwnComment ? "0" : "auto")};

  &:hover .delete-button {
    opacity: 1;
  }
`;

const DeleteButton = styled(Button)`
  position: absolute;
  right: 8px;
  top: 8px;
  opacity: 0;
  transition: opacity 0.2s;
  padding: 4px;
  height: auto;
  line-height: 1;
`;

const GroupSelector = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  cursor: pointer;
  background-color: ${props => (props.isSelected ? "var(--neutral-alt)" : "transparent")};
  border: 1px solid ${props => (props.isSelected ? "var(--neutral-alter)" : "transparent")};
  border-radius: 8px;
  transition: background-color 0.3s;

  &:hover {
    background-color: var(--neutral-alt);
  }

  .ant-avatar {
    background-color: ${props => {
      if (props.isSelected) {
        return "var(--primary-color)";
      }
      return "var(--neutral-alter)";
    }};
    color: ${props => (props.isSelected ? "white" : "inherit")};
  }
`;

const InputContainer = styled.div`
  padding: 20px;
  border-top: 1px solid #f0f0f0;
`;

const CompanyComments = ({ companyId }: CompanyCommentsProps) => {
  const [comments, setComments] = useState<CompanyComment[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<number>(0);
  const [newComment, setNewComment] = useState("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<CompanyComment | null>(null);
  const { width } = useViewport();
  const { currentUser } = useLoginContext();
  const isMobile = width < 768;
  const [isPosting, setIsPosting] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    axios.get<CompanyComment[]>(`/companies/${companyId}/comments/`).then(response => {
      setComments(response.data.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
    });
  }, [companyId]);

  const filteredComments = comments.filter(comment => selectedGroup === 0 || comment.comment_type === selectedGroup);

  const handleSendComment = () => {
    if (!newComment.trim()) return;
    setIsPosting(true);
    axios
      .post(`/companies/${companyId}/comments/`, {
        content: newComment,
        comment_type: selectedGroup,
        created_at: dayjs().toISOString(),
      })
      .then(response => {
        setComments([response.data, ...comments]);
        setNewComment("");
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
      })
      .catch(() => {
        message("error", "Kunne ikke sende kommentaren");
      })
      .finally(() => {
        setIsPosting(false);
      });
  };

  const handleDeleteClick = (comment: CompanyComment) => {
    setCommentToDelete(comment);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (!commentToDelete) return;

    axios
      .delete(`/companies/${companyId}/comments/${commentToDelete.id}/`)
      .then(() => {
        setComments(comments.filter(c => c.id !== commentToDelete.id));
        message("success", "Kommentaren ble slettet");
      })
      .catch(() => {
        message("error", "Kunne ikke slette kommentaren");
      });

    setDeleteModalVisible(false);
    setCommentToDelete(null);
  };

  const groups = [
    { id: 0, name: "Alle kommentarer", icon: <CommentOutlined style={{ fontSize: "24px" }} /> },
    { id: 1, name: "HA", icon: <TeamOutlined style={{ fontSize: "24px" }} /> },
    { id: 2, name: "KarriereDagene", icon: <BankOutlined style={{ fontSize: "24px" }} /> },
  ];

  return (
    <>
      <Card style={{ width: "100%" }} title="Kommentarer">
        <Row gutter={[24, 24]}>
          {!isMobile && (
            <Col span={6}>
              {groups.map(group => (
                <GroupSelector
                  key={group.id}
                  isSelected={selectedGroup === group.id}
                  onClick={() => setSelectedGroup(group.id)}
                >
                  <Avatar size={64} icon={group.icon} style={{ marginBottom: "8px" }} />
                  <Typography.Text strong>{group.name}</Typography.Text>
                </GroupSelector>
              ))}
            </Col>
          )}

          <Col span={isMobile ? 24 : 18}>
            {isMobile && (
              <Row gutter={[16, 16]}>
                {groups.map(group => (
                  <Col span={8} key={group.id}>
                    <GroupSelector isSelected={selectedGroup === group.id} onClick={() => setSelectedGroup(group.id)}>
                      <Avatar size={48} icon={group.icon} style={{ marginBottom: "8px" }} />
                      <Typography.Text strong>{group.name}</Typography.Text>
                    </GroupSelector>
                  </Col>
                ))}
              </Row>
            )}

            <ChatContainer ref={chatContainerRef}>
              {filteredComments.map(comment => {
                const isOwnComment = currentUser?.id === comment.bl_member_id;
                return (
                  <div
                    key={comment.id}
                    style={{
                      display: "flex",
                      marginBottom: "16px",
                      flexDirection: isOwnComment ? "row-reverse" : "row",
                    }}
                  >
                    <Avatar
                      src={comment.bl_member_profile_picture ?? fallbackAvatar}
                      size={40}
                      style={{ marginRight: isOwnComment ? "0" : "12px", marginLeft: isOwnComment ? "12px" : "0" }}
                    />
                    <div style={{ flex: 1 }}>
                      <Typography.Text strong style={{ display: "block", textAlign: isOwnComment ? "right" : "left" }}>
                        {comment.bl_member_name || "Ukjent medlem"}
                      </Typography.Text>
                      <ChatBubble isMobile={isMobile} isOwnComment={isOwnComment}>
                        <DeleteButton
                          className="delete-button"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteClick(comment)}
                        />
                        <Typography.Text style={{ color: isOwnComment ? "white" : "inherit" }}>
                          {comment.content}
                        </Typography.Text>
                        <Typography.Text
                          type="secondary"
                          style={{
                            fontSize: "12px",
                            position: "absolute",
                            right: "8px",
                            bottom: "4px",
                            color: isOwnComment ? "rgba(255, 255, 255, 0.7)" : "inherit",
                          }}
                        >
                          {dayjs(comment.created_at).format("DD.MM.YYYY HH:mm")}
                        </Typography.Text>
                      </ChatBubble>
                    </div>
                  </div>
                );
              })}
            </ChatContainer>

            <InputContainer>
              <Row gutter={[8, 8]}>
                <Col flex="auto">
                  <Input.TextArea
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    placeholder="Skriv en kommentar..."
                    autoSize={{ minRows: 1, maxRows: 6 }}
                  />
                </Col>
                <Col>
                  <Button type="primary" onClick={handleSendComment} loading={isPosting}>
                    Send
                  </Button>
                </Col>
              </Row>
            </InputContainer>
          </Col>
        </Row>
      </Card>

      <Modal
        title="Slett kommentar"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalVisible(false);
          setCommentToDelete(null);
        }}
        okText="Slett"
        cancelText="Avbryt"
        okButtonProps={{ danger: true }}
      >
        <Typography.Text>
          Er du sikker på at du vil slette denne kommentaren? Denne handlingen kan ikke angres.
        </Typography.Text>
      </Modal>
    </>
  );
};

export default CompanyComments;
